import {
  collection, addDoc, doc, query, where, orderBy, limit, onSnapshot, updateDoc, writeBatch,
  serverTimestamp, arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';

// Notifications in-app (sans backend) : écrites par le client au moment de l'événement,
// lues en temps réel par les destinataires. Deux audiences :
//  - 'architects' : propriétaire + membres architectes du cabinet
//  - 'user'       : un compte précis (toUid), ex. technicien responsable du dossier
// La liste des types doit rester identique à la liste blanche des règles Firestore.
export const NOTIF_TYPES = {
  MESSAGE: 'message',
  DOSSIER_NEW: 'dossier.nouveau',
  DOSSIER_ASSIGNED: 'dossier.affectation',
  AVIS_FAVORABLE: 'avis.favorable',
  AVIS_DEFAVORABLE: 'avis.defavorable'
};

export const NOTIF_LABELS = {
  'message': 'Nouveau message',
  'dossier.nouveau': 'Nouveau dossier',
  'dossier.affectation': 'Dossier affecté',
  'avis.favorable': 'Avis favorable',
  'avis.defavorable': 'Avis défavorable'
};

let ctx = { cabinetId: null, me: { uid: null, name: '', role: '' }, members: [], canFull: false };

const memberUidByName = (name) => {
  if (!name) return null;
  const m = ctx.members.find((x) => x.isActive !== false && x.agentName === name);
  return m ? m.id : null;
};

const write = async (data) => {
  try {
    await addDoc(collection(db, 'notifications'), {
      cabinetId: ctx.cabinetId,
      fromUid: ctx.me.uid,
      fromName: ctx.me.name || '',
      readBy: [],
      createdAt: serverTimestamp(),
      ...data
    });
  } catch (e) {
    console.warn('Notification non enregistrée:', e.message);
  }
};

export class NotificationService {
  static configure(next) {
    ctx = { ...ctx, ...next };
  }

  // Émission générique : audience architectes + technicien ciblé par nom (si connu)
  static async emit({ type, title, body = '', project = null, toArchitects = true, toAgentName = null, toUids = [] }) {
    if (!ctx.cabinetId || !ctx.me.uid) return;
    const base = {
      type,
      title: (title || '').slice(0, 140),
      body: (body || '').slice(0, 300),
      projectId: project?.id || '',
      refGlobal: project?.refGlobal || ''
    };
    const jobs = [];
    if (toArchitects) jobs.push(write({ ...base, audience: 'architects' }));
    // Cibler un utilisateur : réservé aux accès complets (règle), jamais soi-même
    if (ctx.canFull) {
      const targets = new Set(toUids.filter(Boolean));
      const techUid = memberUidByName(toAgentName);
      if (techUid) targets.add(techUid);
      targets.delete(ctx.me.uid);
      targets.forEach((uid) => jobs.push(write({ ...base, audience: 'user', toUid: uid })));
    }
    await Promise.all(jobs);
  }

  static message(project, text) {
    const mentionUids = [];
    return NotificationService.emit({
      type: NOTIF_TYPES.MESSAGE,
      title: `${ctx.me.name || 'Un agent'} · ${project.refGlobal || project.client || 'dossier'}`,
      body: text,
      project,
      toAgentName: project.agentResponsable,
      toUids: mentionUids
    });
  }

  static dossierNew(project) {
    return NotificationService.emit({
      type: NOTIF_TYPES.DOSSIER_NEW,
      title: `${project.refGlobal || ''} · ${project.client || ''}`.trim(),
      body: [project.natureProjet, project.commune, project.agentResponsable ? `Agent : ${project.agentResponsable}` : ''].filter(Boolean).join(' · '),
      project,
      toAgentName: project.agentResponsable
    });
  }

  static dossierAssigned(project) {
    return NotificationService.emit({
      type: NOTIF_TYPES.DOSSIER_ASSIGNED,
      title: `${project.refGlobal || ''} · ${project.client || ''}`.trim(),
      body: [project.natureProjet, project.commune].filter(Boolean).join(' · '),
      project,
      toArchitects: false,
      toAgentName: project.agentResponsable
    });
  }

  static avis(project, statut, remarque) {
    const favorable = statut === 'Favorable';
    return NotificationService.emit({
      type: favorable ? NOTIF_TYPES.AVIS_FAVORABLE : NOTIF_TYPES.AVIS_DEFAVORABLE,
      title: `${project.refGlobal || ''} · ${project.client || ''}`.trim(),
      body: remarque ? `Remarques : ${remarque}` : '',
      project,
      toAgentName: project.agentResponsable
    });
  }

  // Abonnement : notifications personnelles + (architectes) celles du cabinet
  static subscribe({ cabinetId, uid, isArchitect }, callback) {
    const buckets = { user: [], architects: [] };
    const push = () => {
      const seen = new Set();
      const all = [...buckets.user, ...buckets.architects]
        .filter((n) => (seen.has(n.id) ? false : seen.add(n.id)))
        .filter((n) => n.fromUid !== uid)
        .sort((a, b) => (b.createdAt?.toMillis?.() || Date.now()) - (a.createdAt?.toMillis?.() || Date.now()))
        .slice(0, 50);
      callback(all);
    };
    const onErr = (e) => console.warn('Notifications:', e.message);
    const unsubs = [];
    try {
      unsubs.push(onSnapshot(
        query(collection(db, 'notifications'), where('toUid', '==', uid), orderBy('createdAt', 'desc'), limit(50)),
        (snap) => { buckets.user = snap.docs.map((d) => ({ id: d.id, ...d.data() })); push(); }, onErr
      ));
      if (isArchitect) {
        unsubs.push(onSnapshot(
          query(collection(db, 'notifications'), where('cabinetId', '==', cabinetId), where('audience', '==', 'architects'), orderBy('createdAt', 'desc'), limit(50)),
          (snap) => { buckets.architects = snap.docs.map((d) => ({ id: d.id, ...d.data() })); push(); }, onErr
        ));
      }
    } catch (e) {
      onErr(e);
    }
    return () => unsubs.forEach((u) => u());
  }

  static isUnread(n, uid) {
    return !(n.readBy || []).includes(uid);
  }

  static async markRead(id, uid) {
    try {
      await updateDoc(doc(db, 'notifications', id), { readBy: arrayUnion(uid) });
    } catch (e) {
      console.warn('Lecture non enregistrée:', e.message);
    }
  }

  static async markAllRead(items, uid) {
    const unread = items.filter((n) => NotificationService.isUnread(n, uid));
    if (!unread.length) return;
    try {
      const batch = writeBatch(db);
      unread.forEach((n) => batch.update(doc(db, 'notifications', n.id), { readBy: arrayUnion(uid) }));
      await batch.commit();
    } catch (e) {
      console.warn('Lecture non enregistrée:', e.message);
    }
  }
}
